(() => {
  const selector = '#checkout, #CartDrawer-Checkout';
  const originalFetch = window.fetch.bind(window);
  const savedStates = new WeakMap();
  let pending = 0;
  let busy = false;
  let idleTimer;
  // Checkout click made while the cart was saving; replayed once it is idle.
  let queuedCheckoutId = null;

  function deliveryBlocked(button) {
    return Boolean(window.NP && button.matches(window.NP.selectorCheckoutButton) &&
      typeof window.isNovaPoshtaDeliveryValid === 'function' && !window.isNovaPoshtaDeliveryValid());
  }

  // The button is not disabled while busy: pressing it blurs the delivery fields,
  // which save the address to the cart, and a disabled button would drop that click.
  function render() {
    document.documentElement.classList.toggle('cart-checkout-busy', busy);
    document.querySelectorAll(selector).forEach((button) => {
      if (busy) {
        if (!savedStates.has(button)) {
          savedStates.set(button, { ariaBusy: button.getAttribute('aria-busy') });
        }
        if (button.getAttribute('aria-busy') !== 'true') button.setAttribute('aria-busy', 'true');
      } else if (savedStates.has(button)) {
        const state = savedStates.get(button);
        if (state.ariaBusy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', state.ariaBusy);
        savedStates.delete(button);
      }
    });
  }

  function queueCheckout(button) {
    queuedCheckoutId = button.id;
  }

  function replayQueuedCheckout() {
    if (!queuedCheckoutId) return;
    const button = document.getElementById(queuedCheckoutId);
    queuedCheckoutId = null;
    if (!button || button.disabled) return;
    // Do not jump to checkout if the customer closed the drawer meanwhile.
    const drawer = button.closest('cart-drawer');
    if (drawer && !drawer.classList.contains('active')) return;
    if (deliveryBlocked(button)) showDeliveryNotice(button);
    else button.click();
  }

  function begin() {
    clearTimeout(idleTimer);
    pending++;
    busy = true;
    render();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      pending--;
      if (pending) return;
      // Bridge the theme's debounced quantity and promotion updates (250–400 ms).
      idleTimer = setTimeout(() => {
        if (pending) return;
        busy = false;
        render();
        replayQueuedCheckout();
      }, 450);
    };
  }

  function setDisabled(button, disabled) {
    if (!button) return;
    button.disabled = disabled;
  }

  // An incomplete delivery does not disable the button: the click explains what is missing.
  function showDeliveryNotice(button) {
    window.showNovaPoshtaDeliveryNotice?.(button);
  }

  window.cartCheckoutBusy = { begin, setDisabled };

  function isCartRequest(input) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      return url.origin === location.origin && /\/cart(?:\/(?:add|change|update|clear))?(?:\.js|\.json)?\/?$/.test(url.pathname);
    } catch {
      return false;
    }
  }

  function requestMethod(input, init) {
    return String(init?.method || (input instanceof Request ? input.method : '') || 'GET').toUpperCase();
  }

  // Cart writes: Ajax cart endpoints and Storefront API cart mutations (apps such
  // as Monk write cart metafields after every cart change).
  function isCartMutation(input, init) {
    if (requestMethod(input, init) === 'GET') return false;
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      if (url.origin === location.origin && /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname)) {
        return true;
      }
      return /\/api\/[^/]+\/graphql(?:\.json)?$/.test(url.pathname) &&
        typeof init?.body === 'string' && /\bmutation\b[\s\S]*\bcart[A-Z]\w*\s*\(/.test(init.body);
    } catch {
      return false;
    }
  }

  // Shopify loses or resurrects cart lines when writes overlap, so writes run one
  // at a time and cart reads wait for the pending writes.
  let cartWriteQueue = Promise.resolve();

  // Track all cart callers, including shipping and section rendering requests.
  window.fetch = async (...args) => {
    const mutation = isCartMutation(args[0], args[1]);
    const cartRequest = isCartRequest(args[0]);
    if (!mutation && !cartRequest) return originalFetch(...args);

    const previousWrites = cartWriteQueue;
    let releaseWrite;
    if (mutation) {
      cartWriteQueue = new Promise((resolve) => {
        releaseWrite = resolve;
      });
    }

    const end = cartRequest ? begin() : () => {};
    try {
      await previousWrites;
      const response = await originalFetch(...args);
      releaseWrite?.();
      if (!cartRequest) return response;
      // Track the caller's body read without cloning or buffering a second response.
      for (const method of ['json', 'text']) {
        const read = response[method].bind(response);
        response[method] = async (...readArgs) => {
          const endRead = begin();
          try {
            return await read(...readArgs);
          } finally {
            endRead();
          }
        };
      }
      end();
      return response;
    } catch (error) {
      releaseWrite?.();
      end();
      throw error;
    }
  };

  new MutationObserver((records) => {
    if (!busy) return;
    // Only a newly rendered checkout button needs its state reapplied.
    const hasNewButton = records.some((record) => Array.from(record.addedNodes).some((node) =>
      node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector))
    ));
    if (hasNewButton) render();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('cart-items .quantity__input, cart-drawer-items .quantity__input, [data-auto-bundle-tester-select]')) {
      begin()();
    }
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest(selector);
    if (button && (busy || deliveryBlocked(button))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) queueCheckout(button);
      else showDeliveryNotice(button);
    }
  }, true);

  document.addEventListener('submit', (event) => {
    if (event.submitter?.matches(selector) && (busy || deliveryBlocked(event.submitter))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) queueCheckout(event.submitter);
      else showDeliveryNotice(event.submitter);
    }
  }, true);
})();
